import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry for frontend error tracking
 * Only initializes in production or when VITE_SENTRY_DSN is set
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.MODE || 'development';

  if (!dsn) {
    console.log('[Sentry] DSN not configured, skipping initialization');
    return;
  }

  Sentry.init({
    dsn,
    environment,
    release: import.meta.env.VITE_APP_VERSION || 'unknown',

    // Integrations
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Capture 10% of all sessions
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Performance monitoring
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0,

    // Session replay (only errors in production)
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: environment === 'production' ? 1.0 : 0,

    // Filter out known non-errors
    beforeSend(event, hint) {
      const error = hint?.originalException;

      // Ignore network errors
      if (error instanceof Error) {
        if (error.message.includes('Failed to fetch')) {
          return null;
        }
        if (error.message.includes('NetworkError')) {
          return null;
        }
        // Ignore chunk load errors (handled by retry logic)
        if (error.message.includes('ChunkLoadError')) {
          return null;
        }
      }

      return event;
    },

    // Sensitive data filtering
    beforeBreadcrumb(breadcrumb) {
      // Remove sensitive XHR data
      if (breadcrumb.category === 'xhr' && breadcrumb.data) {
        delete breadcrumb.data.response;
      }
      return breadcrumb;
    },
  });

  console.log(`[Sentry] Initialized for environment: ${environment}`);
}

/**
 * Capture an exception to Sentry
 */
export function captureException(error: Error, context?: Record<string, unknown>): void {
  if (!import.meta.env.VITE_SENTRY_DSN) return;

  Sentry.withScope((scope) => {
    if (context) {
      scope.setExtras(context);
    }
    Sentry.captureException(error);
  });
}

/**
 * Capture a message to Sentry
 */
export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info'): void {
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  Sentry.captureMessage(message, level);
}

/**
 * Set user context for Sentry
 */
export function setUser(user: { id: string; email?: string } | null): void {
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  Sentry.setUser(user);
}

export { Sentry };
